/**
 * Lossless mapper between a flow definition and the canvas graph.
 *
 * Two definition shapes exist and both round-trip exactly:
 *
 *   v1, a chain      { trigger, nodes: [ {type, …}, … ] }
 *                    nodes run in array order. Node ORDER is the only
 *                    structural truth, edges only encode that order, and
 *                    positions are not persisted (the layout is deterministic).
 *
 *   v2, a graph      { version: 2, trigger, nodes: [ {id, type, name, ui?, …} ],
 *                      edges: [ {from, to, branch?} ] }
 *                    a node's successor is the edge leaving it; a branching
 *                    node (Yes / No, one per option, Success / Error) picks by
 *                    `branch`. Every node carries an `id` because edges point
 *                    at them, and `ui: {x, y}` keeps where the author put it.
 *
 * The builder writes v1 for a plain chain (so a flow made before branching
 * existed is saved back byte-for-byte) and v2 as soon as anything branches,
 * or as soon as the definition already was v2.
 *
 * Guarantee: `graphToFlow(flowToGraph(d))` deep-equals `d` for any JSON
 * object `d`. Unknown node kinds, non-object entries, extra top-level keys and
 * a non-object `trigger` all pass through untouched.
 *
 * Pure functions, no DOM, no runtime imports, erasable TS only:
 * `scripts/smoke/30-flow-builder.sh` runs this file under Node's type
 * stripping to produce the definition it posts.
 */
import type { FlowEdgeData, FlowNodeData } from '@repletoryreach/sdk-ui/dnd';

export type JsonObject = Record<string, unknown>;
export type FlowDefinition = JsonObject;

/** Canvas id + type of the single trigger node. Reserved even when no trigger exists. */
export const TRIGGER_NODE_ID = 'trigger';
export const TRIGGER_NODE_TYPE = 'trigger';
/** Canvas type for `nodes[]` entries the mapper can't interpret (non-object, or no string `type`). */
export const RAW_NODE_TYPE = '__raw';
const RAW_KEY = '__raw';

/** Every node kind the engine executes. `reply` is the v1 name for `send`. */
export const ENGINE_NODE_KINDS = [
  'send', 'template', 'reply', 'ask', 'form', 'llm', 'http', 'contact', 'handoff', 'notify', 'sms', 'email', 'flow',
  'set', 'condition', 'decision', 'switch', 'hours', 'split', 'delay', 'end', 'booking', 'manage_booking', 'ticket', 'ticket_status', 'lookup',
  'language'
] as const;
export type EngineNodeKind = (typeof ENGINE_NODE_KINDS)[number];
export const TRIGGER_KINDS = ['keyword', 'message', 'always', 'webhook'] as const;
export type TriggerKind = (typeof TRIGGER_KINDS)[number];

export function isEngineNodeKind(type: string): type is EngineNodeKind {
  return (ENGINE_NODE_KINDS as readonly string[]).includes(type);
}

export interface BuilderNode extends FlowNodeData {
  /** The engine node's fields minus `type` (or `{ __raw }` for opaque entries). */
  data: JsonObject;
}

export interface BuilderGraph {
  nodes: BuilderNode[];
  edges: FlowEdgeData[];
  /** Top-level definition keys other than `trigger` / `nodes` / `edges`: kept verbatim (plus non-object trigger/nodes passthroughs). */
  extra: JsonObject;
  /** Whether the definition carried an array `nodes` key (`{ nodes: [] }` must not collapse to `{}`). */
  hasNodes: boolean;
  /** Whether the definition carried an array `edges` key: it is a v2 graph and is written back as one. */
  hasEdges: boolean;
}

export const LAYOUT = {
  startX: 48,
  rowY: 48,
  nodeWidth: 300,
  nodeHeight: 84,
  gapX: 56,
  rowGap: 110
} as const;

/**
 * Where a node sits for a given column and row. Vertical reading direction:
 * a row is a level of the graph (the trigger is row 0), a column is a sibling
 * at that level.
 */
export function layoutPosition(column: number, row = 0): { x: number; y: number } {
  return {
    x: LAYOUT.startX + column * (LAYOUT.nodeWidth + LAYOUT.gapX),
    y: LAYOUT.rowY + row * (LAYOUT.nodeHeight + LAYOUT.rowGap)
  };
}

export function isPlainObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** JSON-semantics deep clone (definitions come from / go to JSON). */
export function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

export function edgeId(source: string, target: string, branch?: string): string {
  return branch ? `e:${source}->${target}:${branch}` : `e:${source}->${target}`;
}

/** The colour a branch pill takes: green for the happy path, amber for the other, grey when it is neither. */
export function branchTone(branch: string): 'positive' | 'negative' | 'neutral' {
  const b = branch.toLowerCase();
  if (['yes', 'true', 'answered', 'found', 'open', 'success', 'submitted', 'agent', 'matched', 'ok', 'changed', 'a'].includes(b)) return 'positive';
  if (['no', 'false', 'unsure', 'missing', 'closed', 'error', 'failure', 'failed', 'cancelled', 'invalid', 'no_agent', 'timeout', 'other', 'else', 'otherwise', 'b'].includes(b)) return 'negative';
  return 'neutral';
}

/** A default (unlabelled) link, as a chain uses. */
export function chainEdge(source: string, target: string): FlowEdgeData {
  return { id: edgeId(source, target), source, target };
}

/** A link leaving a named output port. */
export function branchEdge(source: string, target: string, branch: string): FlowEdgeData {
  return { id: edgeId(source, target, branch), source, target, sourceHandle: branch, label: branch, data: { tone: branchTone(branch) } };
}

function preferredId(raw: unknown, index: number): string {
  const engineId = isPlainObject(raw) && (typeof raw.id === 'string' || typeof raw.id === 'number') ? String(raw.id).trim() : '';
  return engineId || `n${index + 1}`;
}

function uniqueId(base: string, used: Set<string>): string {
  if (!used.has(base)) return base;
  let i = 2;
  while (used.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
}

function readUi(raw: JsonObject): { x: number; y: number } | null {
  const ui = raw.ui;
  if (!isPlainObject(ui)) return null;
  const x = Number(ui.x);
  const y = Number(ui.y);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function toBuilderNode(id: string, raw: unknown, position: { x: number; y: number }): BuilderNode {
  if (isPlainObject(raw) && typeof raw.type === 'string') {
    const { type, ...rest } = raw;
    return { id, type, position, width: LAYOUT.nodeWidth, height: LAYOUT.nodeHeight, data: clone(rest) };
  }
  return { id, type: RAW_NODE_TYPE, position, width: LAYOUT.nodeWidth, height: LAYOUT.nodeHeight, data: { [RAW_KEY]: clone(raw) } };
}

/** The engine node for a canvas node. `withId` forces an `id` (v2); `withUi` records the position (v2). */
function toEngineNode(node: BuilderNode, withId: boolean, withUi: boolean): unknown {
  if (node.type === RAW_NODE_TYPE) return clone(node.data[RAW_KEY]);
  const rest: JsonObject = {};
  for (const [k, v] of Object.entries(node.data)) if (k !== 'type' && k !== 'ui') rest[k] = v;
  const out: JsonObject = { type: node.type };
  if (withId) out.id = node.id;
  else if ('id' in rest) out.id = rest.id;
  delete rest.id;
  Object.assign(out, clone(rest));
  if (withUi) out.ui = { x: Math.round(node.position.x), y: Math.round(node.position.y) };
  else if (isPlainObject(node.data.ui)) out.ui = clone(node.data.ui);
  return out;
}

/** definition → graph. Never mutates its input. */
export function flowToGraph(definition: unknown): BuilderGraph {
  const def: JsonObject = isPlainObject(definition) ? definition : {};
  const extra: JsonObject = {};
  for (const [k, v] of Object.entries(def)) if (k !== 'trigger' && k !== 'nodes' && k !== 'edges') extra[k] = clone(v);

  const nodes: BuilderNode[] = [];
  const edges: FlowEdgeData[] = [];
  const used = new Set<string>([TRIGGER_NODE_ID]);
  const hasEdges = Array.isArray(def.edges);

  let hasTriggerNode = false;
  if (isPlainObject(def.trigger)) {
    nodes.push({
      id: TRIGGER_NODE_ID,
      type: TRIGGER_NODE_TYPE,
      position: layoutPosition(0),
      isStart: true,
      width: LAYOUT.nodeWidth,
      height: LAYOUT.nodeHeight,
      data: clone(def.trigger)
    });
    hasTriggerNode = true;
  } else if ('trigger' in def) {
    extra.trigger = clone(def.trigger);
  }

  const hasNodes = Array.isArray(def.nodes);
  if (!hasNodes && 'nodes' in def) extra.nodes = clone(def.nodes);
  const list: unknown[] = hasNodes ? (def.nodes as unknown[]) : [];
  const positioned = new Set<string>();
  list.forEach((raw, index) => {
    const id = uniqueId(preferredId(raw, index), used);
    used.add(id);
    const ui = isPlainObject(raw) ? readUi(raw) : null;
    const node = toBuilderNode(id, raw, ui ?? layoutPosition(0, index + 1));
    if (ui) positioned.add(id);
    nodes.push(node);
  });

  if (hasEdges) {
    const ids = new Set(nodes.map((n) => n.id));
    for (const raw of def.edges as unknown[]) {
      if (!isPlainObject(raw)) continue;
      const from = String(raw.from ?? raw.source ?? '');
      const to = String(raw.to ?? raw.target ?? '');
      if (!ids.has(from) || !ids.has(to)) continue;
      const branch = typeof raw.branch === 'string' && raw.branch ? raw.branch : undefined;
      edges.push(branch ? branchEdge(from, to, branch) : chainEdge(from, to));
    }
    // The trigger reaches the start node: the first node nothing points at.
    if (hasTriggerNode) {
      const targets = new Set(edges.map((e) => e.target));
      const start = nodes.find((n) => n.id !== TRIGGER_NODE_ID && !targets.has(n.id)) ?? nodes.find((n) => n.id !== TRIGGER_NODE_ID);
      if (start && !edges.some((e) => e.source === TRIGGER_NODE_ID)) edges.push(chainEdge(TRIGGER_NODE_ID, start.id));
    }
  } else {
    let previous: string | null = hasTriggerNode ? TRIGGER_NODE_ID : null;
    for (const n of nodes) {
      if (n.id === TRIGGER_NODE_ID) continue;
      if (previous) edges.push(chainEdge(previous, n.id));
      previous = n.id;
    }
  }

  const graph: BuilderGraph = { nodes, edges, extra, hasNodes, hasEdges };
  // Anything the author did not place is laid out; what they placed stays.
  return autoLayout(graph, positioned);
}

/**
 * Engine order of the non-trigger nodes: follow the edges from the trigger,
 * breadth first, then any other roots (array order), then leftovers.
 */
export function chainOrder(graph: BuilderGraph): BuilderNode[] {
  const byId = new Map<string, BuilderNode>();
  for (const n of graph.nodes) byId.set(n.id, n);
  const outgoing = new Map<string, string[]>();
  const incoming = new Set<string>();
  for (const e of graph.edges) {
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    const list = outgoing.get(e.source);
    if (list) list.push(e.target);
    else outgoing.set(e.source, [e.target]);
    incoming.add(e.target);
  }
  const visited = new Set<string>();
  const ordered: BuilderNode[] = [];
  const walk = (id: string): void => {
    if (visited.has(id)) return;
    visited.add(id);
    const n = byId.get(id);
    if (!n) return;
    if (n.id !== TRIGGER_NODE_ID) ordered.push(n);
    for (const t of outgoing.get(id) ?? []) walk(t);
  };
  if (byId.has(TRIGGER_NODE_ID)) walk(TRIGGER_NODE_ID);
  for (const n of graph.nodes) if (!incoming.has(n.id)) walk(n.id);
  for (const n of graph.nodes) walk(n.id);
  return ordered;
}

/**
 * Whether the graph is one plain chain: no branches, no port edges, every
 * node with at most one way in and one way out. Such a graph is written as
 * v1, which is what keeps an old flow byte-identical after a round trip.
 */
export function isSimpleChain(graph: BuilderGraph): boolean {
  if (graph.hasEdges) return false;
  // A `version` key on its own is passed through like any other extra key:
  // only edges, or a structure edges are needed for, make a v2 document.
  const outCount = new Map<string, number>();
  const inCount = new Map<string, number>();
  for (const e of graph.edges) {
    if (e.sourceHandle) return false;
    outCount.set(e.source, (outCount.get(e.source) ?? 0) + 1);
    inCount.set(e.target, (inCount.get(e.target) ?? 0) + 1);
  }
  for (const c of outCount.values()) if (c > 1) return false;
  for (const c of inCount.values()) if (c > 1) return false;
  return true;
}

/** graph → definition. Never mutates its input. */
export function graphToFlow(graph: BuilderGraph): FlowDefinition {
  const def: FlowDefinition = {};
  const v2 = !isSimpleChain(graph);

  if (v2 && !('version' in graph.extra)) def.version = 2;
  const trigger = graph.nodes.find((n) => n.id === TRIGGER_NODE_ID);
  if (trigger) def.trigger = clone(trigger.data);
  else if ('trigger' in graph.extra) def.trigger = clone(graph.extra.trigger);

  // In a chain, array order IS the structure, so it is rebuilt from the
  // edges. In a graph the edges carry the structure and the array keeps the
  // order the author (or the file) had, which is what makes the trip exact.
  const ordered = v2 ? graph.nodes.filter((n) => n.id !== TRIGGER_NODE_ID) : chainOrder(graph);
  if (graph.hasNodes || ordered.length > 0) def.nodes = ordered.map((n) => toEngineNode(n, v2, v2));
  else if ('nodes' in graph.extra) def.nodes = clone(graph.extra.nodes);

  if (v2) {
    def.edges = graph.edges
      .filter((e) => e.source !== TRIGGER_NODE_ID && graph.nodes.some((n) => n.id === e.target) && graph.nodes.some((n) => n.id === e.source))
      .map((e) => (e.sourceHandle ? { from: e.source, to: e.target, branch: e.sourceHandle } : { from: e.source, to: e.target }));
  }

  for (const [k, v] of Object.entries(graph.extra)) {
    if (k === 'trigger' || k === 'nodes' || k === 'edges') continue;
    def[k] = clone(v);
  }
  return def;
}

/**
 * Layered layout, top to bottom: the trigger on row 0, each node one row
 * below the deepest node that points at it, siblings side by side. Nodes in
 * `keep` are left where they are; nodes nothing reaches go on the right.
 */
export function autoLayout(graph: BuilderGraph, keep: ReadonlySet<string> = new Set()): BuilderGraph {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const n of graph.nodes) indegree.set(n.id, 0);
  for (const e of graph.edges) {
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    (outgoing.get(e.source) ?? outgoing.set(e.source, []).get(e.source)!).push(e.target);
    indegree.set(e.target, (indegree.get(e.target) ?? 0) + 1);
  }

  // Longest-path levels from the roots, cycles broken by visitation order.
  const level = new Map<string, number>();
  const roots = graph.nodes.filter((n) => (indegree.get(n.id) ?? 0) === 0).map((n) => n.id);
  if (byId.has(TRIGGER_NODE_ID)) roots.sort((a, b) => (a === TRIGGER_NODE_ID ? -1 : b === TRIGGER_NODE_ID ? 1 : 0));
  const queue = [...roots];
  for (const r of roots) level.set(r, 0);
  const seen = new Set<string>();
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const l = level.get(id) ?? 0;
    for (const t of outgoing.get(id) ?? []) {
      level.set(t, Math.max(level.get(t) ?? 0, l + 1));
      queue.push(t);
    }
  }
  let spare = 0;
  for (const n of graph.nodes) if (!level.has(n.id)) level.set(n.id, spare++ + 1);

  // Column: siblings at a level, in edge order, centred under nothing in
  // particular; a single node at a level sits under its parent's column.
  const rows = new Map<number, string[]>();
  const order = chainOrder(graph).map((n) => n.id);
  if (byId.has(TRIGGER_NODE_ID)) order.unshift(TRIGGER_NODE_ID);
  for (const id of order) {
    const l = level.get(id) ?? 0;
    (rows.get(l) ?? rows.set(l, []).get(l)!).push(id);
  }
  for (const n of graph.nodes) {
    const l = level.get(n.id) ?? 0;
    const row = rows.get(l) ?? rows.set(l, []).get(l)!;
    if (!row.includes(n.id)) row.push(n.id);
  }

  const parentColumn = new Map<string, number>();
  const nodes = graph.nodes.map((n) => {
    if (keep.has(n.id)) return { ...n, width: n.width ?? LAYOUT.nodeWidth, height: n.height ?? LAYOUT.nodeHeight };
    const l = level.get(n.id) ?? 0;
    const row = rows.get(l) ?? [];
    let column = row.indexOf(n.id);
    if (row.length === 1) {
      // Alone on its row: sit under the node that leads here.
      const parent = graph.edges.find((e) => e.target === n.id)?.source;
      column = parent !== undefined ? (parentColumn.get(parent) ?? 0) : 0;
    }
    parentColumn.set(n.id, column);
    return { ...n, position: layoutPosition(column, l), width: n.width ?? LAYOUT.nodeWidth, height: n.height ?? LAYOUT.nodeHeight };
  });
  // A second pass so children placed before their parent's column was known
  // still line up.
  const byIdOut = new Map(nodes.map((n) => [n.id, n]));
  for (const n of nodes) {
    if (keep.has(n.id)) continue;
    const l = level.get(n.id) ?? 0;
    const row = rows.get(l) ?? [];
    if (row.length !== 1) continue;
    const parent = graph.edges.find((e) => e.target === n.id)?.source;
    const p = parent ? byIdOut.get(parent) : undefined;
    if (p) n.position = { x: p.position.x, y: layoutPosition(0, l).y };
  }
  return { ...graph, nodes };
}
